import commonStyles from './Dropdowns.module.css';
import clsx from 'clsx';
import { useRefWidth } from '@upright/ui/common/generic/hooks';
import { CSSProperties, RefObject } from 'react';
import { Popover, PopoverProps, ListBox as AriaListBox, ListBoxProps } from 'react-aria-components';

export type Props<T extends object> = Pick<PopoverProps, 'placement'> &
  /*
   * `renderEmptyState` is what an open-but-empty list says. A picker never needs it — it has its
   * options before it opens — but a search opens on the first keystroke, before any results exist,
   * and a popover with nothing in it is a bug the user has to interpret.
   */
  Pick<ListBoxProps<T>, 'items' | 'children' | 'renderEmptyState'> & {
    shouldMatchTriggerWidth?: boolean;
    /**
     * The box the popover follows: where it is placed, and — through `--trigger-width` — how wide
     * `shouldMatchTriggerWidth` makes it. Both otherwise come from whichever element react-aria's
     * trigger component nominates, which for `Select` is its button, the box the user sees, and for
     * `ComboBox` is the bare `input`. A field that draws a box around that input is wider than it by
     * however much its adornments and padding take, so a popover asked to match "the trigger" came
     * out narrower than the field and inset from its edge: 342px under a 360px search field. A
     * component that draws such a box passes it here.
     */
    triggerRef?: RefObject<HTMLElement | null>;
    /**
     * With `shouldMatchTriggerWidth`, the narrowest the popover may get, as a CSS length: it is as
     * wide as the trigger or this, whichever is wider. For a field that shrinks below the width its
     * rows need to stay distinguishable, as the shell's search does in a narrow top bar. Pair it
     * with a `bottom end` placement for a trigger at the right of the screen, so the extra width
     * grows away from the edge.
     */
    popoverMinInlineSize?: string;
  };

export function ListBox<T extends object>({
  placement,
  triggerRef,
  shouldMatchTriggerWidth,
  popoverMinInlineSize,
  items,
  children,
  renderEmptyState,
}: Props<T>) {
  /*
   * Measured here rather than left to the ref, because the two are separate mechanisms in react-aria
   * and only the placement one reads the ref. `ComboBox` computes `--trigger-width` itself, as the
   * union of the input's and the toggle button's rects ("Make menu width match input + button"), and
   * publishes it in the style it puts on the popover's context — where `usePopover` never looks. So
   * the property has to be overridden to be corrected, and `style` is what does it: the popover
   * applies its own `style` prop after react-aria's own positioning styles.
   */
  const triggerWidth = useRefWidth(triggerRef);
  const popoverStyle: CSSProperties & Partial<Record<'--trigger-width' | '--ds-popover-min-inline-size', string>> = {
    ...(triggerWidth > 0 ? { '--trigger-width': `${triggerWidth}px` } : {}),
    ...(popoverMinInlineSize !== undefined ? { '--ds-popover-min-inline-size': popoverMinInlineSize } : {}),
  };

  return (
    <Popover
      shouldFlip
      className={clsx(commonStyles.popover, shouldMatchTriggerWidth && commonStyles['match-trigger-width'])}
      placement={placement}
      // Undefined leaves react-aria's own trigger element and width in place: `mergeProps` keeps a
      // context value when the prop is undefined, so a caller with no better box to offer loses nothing.
      triggerRef={triggerRef}
      style={popoverStyle}
    >
      <AriaListBox
        className={commonStyles.list}
        items={items}
        renderEmptyState={
          renderEmptyState &&
          (renderProps => <div className={commonStyles['empty-state']}>{renderEmptyState(renderProps)}</div>)
        }
      >
        {children}
      </AriaListBox>
    </Popover>
  );
}
